import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notificationService } from '@/services/notificationService'
import type { NotificationFilter } from '@/types/notification'

// Hook to get notifications for a user
export function useNotificationsByUser(userId: string, filter?: NotificationFilter) {
  return useQuery({
    queryKey: ['notifications', 'user', userId, filter],
    queryFn: async () => {
      const response = await notificationService.getByUser(userId, filter)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch notifications')
      }
      return response.data
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  })
}

// Hook to get notifications for a role (e.g., 'admin')
export function useNotificationsByRole(role: string, filter?: NotificationFilter) {
  return useQuery({
    queryKey: ['notifications', 'role', role, filter],
    queryFn: async () => {
      const response = await notificationService.getByRole(role, filter)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch notifications')
      }
      return response.data
    },
    enabled: !!role,
    refetchInterval: 30000, // Refetch every 30 seconds for real-time updates
  })
}

// Hook to get unread count for a user
export function useUnreadCountByUser(userId: string) {
  return useQuery({
    queryKey: ['notifications', 'unread', 'user', userId],
    queryFn: async () => {
      const response = await notificationService.getUnreadCountByUser(userId)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch unread count')
      }
      return (response.data as { unreadCount?: number })?.unreadCount || 0
    },
    enabled: !!userId,
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

// Hook to get unread count for a role
export function useUnreadCountByRole(role: string) {
  return useQuery({
    queryKey: ['notifications', 'unread', 'role', role],
    queryFn: async () => {
      const response = await notificationService.getUnreadCountByRole(role)
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch unread count')
      }
      return (response.data as { unreadCount?: number })?.unreadCount || 0
    },
    enabled: !!role,
    refetchInterval: 30000, // Refetch every 30 seconds
  })
}

// Hook to mark a notification as read
export function useMarkAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      // Invalidate all notification queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// Hook to mark all notifications as read for a user
export function useMarkAllAsReadByUser() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => notificationService.markAllAsReadByUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// Hook to mark all notifications as read for a role
export function useMarkAllAsReadByRole() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (role: string) => notificationService.markAllAsReadByRole(role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// Hook to create a notification (admin compose)
export function useCreateNotification() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: Parameters<typeof notificationService.create>[0]) => notificationService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}

// Hook to delete a notification
export function useDeleteNotification() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => notificationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}